import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import {
  DocumentProcessingStatus,
  PROCESSING_STATUS_LABELS,
  ProcessingStatus,
} from '@core/interfaces/TProcessingStatus';
import { Subject, takeUntil } from 'rxjs';

import { ProcessingStatusService } from '@shared/services/processing-status.service';

@Component({
  selector: 'app-processing-status-indicator',
  templateUrl: './processing-status-indicator.component.html',
  styleUrls: ['./processing-status-indicator.component.scss'],
  standalone: true,
  imports: [MatIcon, MatIconButton, MatProgressBar, MatProgressSpinner, MatTooltip],
})
export class ProcessingStatusIndicatorComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() sourceId!: string;
  @Input() showProgressBar = true;
  @Input() showLabel = true;
  @Input() compact = false;
  @Input() autoStartPolling = true;

  @Output() statusChanged = new EventEmitter<DocumentProcessingStatus>();

  status: DocumentProcessingStatus | undefined;
  isRetrying = false;
  isCancelling = false;

  constructor(private processingStatusService: ProcessingStatusService) {}

  ngOnInit(): void {
    if (!this.sourceId) {
      return;
    }

    // Subscribe to status changes
    this.processingStatusService
      .getDocumentStatus$(this.sourceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        const changed = this.status?.processingStatus !== status?.processingStatus;
        this.status = status;
        if (status && changed) {
          this.statusChanged.emit(status);
        }
      });

    // Start polling if auto-start is enabled
    if (this.autoStartPolling) {
      this.startPolling();
    }
  }

  get label(): string {
    if (!this.status) return '';
    return PROCESSING_STATUS_LABELS[this.status.processingStatus];
  }

  get progress(): number {
    return this.status?.progressPercentage ?? 0;
  }

  get isProcessing(): boolean {
    return this.status?.isProcessing ?? false;
  }

  get isComplete(): boolean {
    return this.status?.isComplete ?? false;
  }

  get hasError(): boolean {
    return this.status?.hasError ?? false;
  }

  get isCancellationPending(): boolean {
    return this.status?.processingStatus === ProcessingStatus.CANCELLING;
  }

  get canCancel(): boolean {
    return this.isProcessing && !this.hasError && !this.isCancellationPending;
  }

  get errorMessage(): string {
    return this.status?.processingError ?? 'Processing failed';
  }

  get tooltipText(): string {
    if (!this.status) {
      return '';
    }

    const lines: string[] = [`${this.label} — ${this.progress}%`];

    if (this.status.totalChunks > 0) {
      lines.push(`Chunks: ${this.status.processedChunks} / ${this.status.totalChunks}`);
    }

    if (this.status.embeddingCount > 0) {
      lines.push(`Embeddings: ${this.status.embeddingCount}`);
    }

    if (this.status.queueJobStatus) {
      lines.push(`Queue status: ${this.status.queueJobStatus}`);
    }

    if (this.status.queueJobProgress != null) {
      lines.push(`Queue progress: ${this.status.queueJobProgress}%`);
    }

    if (this.status.queueJobFailedReason) {
      lines.push(`Queue failure: ${this.status.queueJobFailedReason}`);
    }

    if (this.status.queueJobAttemptsMade != null) {
      lines.push(`Queue attempts: ${this.status.queueJobAttemptsMade}`);
    }

    if (this.hasError) {
      lines.push(`Error: ${this.errorMessage}`);
    }

    if (this.status.retryCount > 0) {
      lines.push(`Retries: ${this.status.retryCount}`);
    }

    return lines.join('\n');
  }

  get icon(): string {
    if (!this.status) return '';

    switch (this.status.processingStatus) {
      case ProcessingStatus.COMPLETE:
        return 'check_circle';
      case ProcessingStatus.ERROR:
        return 'error';
      case ProcessingStatus.CANCELLING:
        return 'cancel';
      case ProcessingStatus.CANCELLED:
        return 'block';
      case ProcessingStatus.PENDING:
        return 'schedule';
      case ProcessingStatus.UPLOADED:
        return 'cloud_upload';
      default:
        return 'sync';
    }
  }

  get statusClass(): string {
    if (!this.status) return '';

    switch (this.status.processingStatus) {
      case ProcessingStatus.COMPLETE:
        return 'status-complete';
      case ProcessingStatus.ERROR:
        return 'status-error';
      case ProcessingStatus.CANCELLING:
        return 'status-cancelling';
      case ProcessingStatus.CANCELLED:
        return 'status-cancelled';
      case ProcessingStatus.PENDING:
      case ProcessingStatus.UPLOADED:
        return 'status-pending';
      default:
        return 'status-processing';
    }
  }

  startPolling(): void {
    if (this.sourceId) {
      this.processingStatusService.startDocumentPolling(this.sourceId);
    }
  }

  stopPolling(): void {
    if (this.sourceId) {
      this.processingStatusService.stopDocumentPolling(this.sourceId);
    }
  }

  onRetry(event: MouseEvent): void {
    event.stopPropagation();

    if (!this.sourceId || this.isRetrying) return;

    this.isRetrying = true;

    this.processingStatusService.retryAndPoll(this.sourceId).subscribe({
      next: () => {
        this.isRetrying = false;
      },
      error: err => {
        console.error('Retry failed:', err);
        this.isRetrying = false;
      },
    });
  }

  onCancel(event: MouseEvent): void {
    event.stopPropagation();

    if (!this.sourceId || this.isCancelling || !this.canCancel) return;

    this.isCancelling = true;

    this.processingStatusService.cancelAndPoll(this.sourceId).subscribe({
      next: () => {
        this.isCancelling = false;
      },
      error: err => {
        console.error('Cancel processing failed:', err);
        this.isCancelling = false;
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopPolling();
  }
}
